import { useState, useEffect } from "react";
import { ChevronRight, Triangle, Square } from "lucide-react";
import { getPerformanceCategory } from "../../../utils/helper";
import ScreenshotViewer from "../../common/ScreenshotViewer";
import IssueTitle from "../../common/IssueTitle";
import PerformanceIssueContent from "./PerformanceIssueContent";
import { hasLighthouseIssueContent } from "./performanceIssueHelpers";
import StickyIssueHeader from "../../common/StickyIssueHeader";
import { issueCardOpenShellClass, issueCardBodyClass, issueCardCollapsedClass } from "../../../utils/stickyIssueHeader";

const IssueDetails = ({ knownIssues, screenshot, url }) => {
  const [openIssues, setOpenIssues] = useState({});
  const [selectedRect, setSelectedRect] = useState(null);

  const performanceIssues = Object.entries(knownIssues).filter(
    ([, issue]) => issue.category === "performance" && issue.score !== null,
  );

  useEffect(() => {
    if (selectedRect) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedRect]);

  const handleThumbnailClick = (rect) => {
    setSelectedRect(rect);
  };

  const toggleIssue = (key) => {
    setOpenIssues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!performanceIssues || performanceIssues.length === 0) {
    return (
      <div className="mt-6 p-4 bg-green-100 border border-green-200 rounded-lg">
        <p className="text-green-800 font-semibold">
          No major performance issues detected. Great job!
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="space-y-3">
        {performanceIssues.map(([key, issue]) => {
          const category = getPerformanceCategory(issue.score * 100);
          const isExpandable =
            hasLighthouseIssueContent(key, issue) ||
            (issue.items && issue.items.length > 0) ||
            !!issue.description;
          const hideSeverity = key === "thirdPartiesInsight";

          const isOpen = openIssues[key] && isExpandable;

            return (
            <div
              key={key}
              data-issue-card
              className={isOpen ? issueCardOpenShellClass : issueCardCollapsedClass}
            >
              <StickyIssueHeader
                active={isOpen}
                  className={`flex items-start p-4 ${
                  isOpen
                    ? isExpandable
                      ? "cursor-pointer"
                      : ""
                    : isExpandable
                      ? "cursor-pointer"
                      : ""
                  }`}
                  onClick={() => isExpandable && toggleIssue(key)}
                >
                {!hideSeverity && category === "Poor" && (
                    <Triangle
                      size={16}
                      className="text-red-500 mr-3 mt-1 flex-shrink-0"
                    />
                  )}
                {!hideSeverity && category === "Needs Improvement" && (
                    <Square
                      size={16}
                      className="text-orange-500 mr-3 mt-1 flex-shrink-0"
                    />
                  )}
                  <div className="flex-grow flex justify-between items-center min-w-0 gap-x-4">
                  <IssueTitle issueKey={key} issue={issue} />
                    {issue.display && (
                    <span className="text-sm text-muted-foreground font-medium text-right flex-shrink-0 w-1/3 leading-tight">
                        {issue.display}
                      </span>
                    )}
                  </div>
                  {isExpandable && (
                    <ChevronRight
                      size={20}
                    className={`ml-2 text-muted-foreground flex-shrink-0 transition-transform mt-1 ${
                        openIssues[key] ? "rotate-90" : ""
                      }`}
                    />
                  )}
              </StickyIssueHeader>

              {isOpen && (
                <div className={issueCardBodyClass}>
                  <PerformanceIssueContent
                    issueKey={key}
                    issue={issue}
                                screenshot={screenshot}
                    pageUrl={url}
                    onThumbnailClick={handleThumbnailClick}
                    embedded
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedRect && (
        <ScreenshotViewer
          screenshot={screenshot}
          rect={selectedRect}
          onClose={() => setSelectedRect(null)}
        />
      )}
    </div>
  );
};

export default IssueDetails;
